import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { storage, db } from '../firebase';

/**
 * Upload a PDF document to a machine.
 *
 * @param {object} params
 * @param {File}   params.file        Browser File object (must be application/pdf)
 * @param {string} params.orgId       Organisation ID
 * @param {string} params.machineId   Machine ID
 * @param {string} params.type        'operators_manual' | 'workshop_manual' | 'private'
 * @param {string} params.userId      Uploader's UID (for audit trail)
 * @param {function} params.onProgress  Called with a percentage (0-100)
 * @param {object} [params.replaceDoc]  If replacing an existing doc, the previous doc record
 * @returns {Promise<{docId, url, storagePath}>}
 */
export async function uploadMachineDocument({ file, orgId, machineId, type, userId, onProgress, replaceDoc }) {
  if (file.type !== 'application/pdf') {
    throw new Error('Only PDF files are allowed.');
  }

  // 1. Create the Firestore record first (so we have an ID)
  const docsCollection = collection(db, 'organisations', orgId, 'machines', machineId, 'documents');
  const newDocRef = await addDoc(docsCollection, {
    type,
    name: file.name,
    fileSize: file.size,
    mimeType: file.type,
    uploadedAt: serverTimestamp(),
    uploadedBy: userId,
    uploadStatus: 'uploading',
  });

  // 2. Upload to Storage at a predictable path
  const storagePath = `organisations/${orgId}/machines/${machineId}/documents/${newDocRef.id}.pdf`;
  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, file, { contentType: 'application/pdf' });

  // 3. Wait for upload, reporting progress along the way
  await new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        if (onProgress) {
          const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress(pct);
        }
      },
      (err) => reject(err),
      () => resolve()
    );
  });

  // 4. Get the download URL and update the Firestore record
  const url = await getDownloadURL(storageRef);
  await updateDoc(
    doc(db, 'organisations', orgId, 'machines', machineId, 'documents', newDocRef.id),
    { url, storagePath, uploadStatus: 'ready' }
  );

  // 5. If replacing, delete the previous doc (both Firestore + Storage)
  if (replaceDoc?.id) {
    try {
      if (replaceDoc.storagePath) {
        await deleteObject(ref(storage, replaceDoc.storagePath));
      }
      await deleteDoc(doc(db, 'organisations', orgId, 'machines', machineId, 'documents', replaceDoc.id));
    } catch (err) {
      console.warn('Failed to clean up replaced document', err);
      // Don't fail the whole upload if cleanup fails — the new doc is up
    }
  }

  return { docId: newDocRef.id, url, storagePath };
}

/**
 * Delete a document (Firestore record + Storage file).
 */
export async function deleteMachineDocument({ orgId, machineId, docRecord }) {
  if (docRecord.storagePath) {
    try {
      await deleteObject(ref(storage, docRecord.storagePath));
    } catch (err) {
      console.warn('Storage delete failed (may already be gone)', err);
    }
  }
  await deleteDoc(doc(db, 'organisations', orgId, 'machines', machineId, 'documents', docRecord.id));
}
